"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.mapSkyLifestyleToFormData = exports.mapFormDataToSkyLifestyle = exports.mapPhoneToSkyLifestyle = exports.normalizeLoanTerm = void 0;
// Data mapping utilities for Sky Lifestyle loan structure
var firestore_1 = require("firebase/firestore");
var loan_application_1 = require("../types/loan-application");
var firebase_1 = require("../utils/firebase");
var DEFAULT_COUNTRY_CODE = "+264";
var DEFAULT_COUNTRY_ABBREVIATION = "NA";
var createEmptyPhone = function () { return ({
    phone_number: "",
    country_code: DEFAULT_COUNTRY_CODE,
    country_abbreviation: DEFAULT_COUNTRY_ABBREVIATION,
}); };
var hasPhoneNumber = function (phone) {
    return !!(phone === null || phone === void 0 ? void 0 : phone.phone_number) && phone.phone_number.trim().length > 0;
};
var sanitizePhoneNumber = function (phoneNumber) {
    return phoneNumber ? phoneNumber.trim() : "";
};
/**
 * Normalizes loan terms to uppercase format matching Sky CRM
 * Handles backward compatibility with old format ("1 Month" → "1 MONTH")
 * @param term - The loan term to normalize
 * @returns Normalized loan term in uppercase format
 */
var normalizeLoanTerm = function (term) {
    if (!term)
        return loan_application_1.DEFAULT_LOAN_TERM;
    // Convert to uppercase to match Sky CRM format
    var normalized = term.toUpperCase();
    // Validate it matches expected format (handles both "1 Month" and "1 MONTH")
    return loan_application_1.LOAN_TERMS_VALUES.includes(normalized)
        ? normalized
        : loan_application_1.DEFAULT_LOAN_TERM;
};
exports.normalizeLoanTerm = normalizeLoanTerm;
// Helper function to convert date to ISO string
var toISOString = function (date) {
    if (!date)
        return undefined;
    if (date instanceof firestore_1.Timestamp) {
        return date.toDate().toISOString();
    }
    if (date instanceof Date) {
        return date.toISOString();
    }
    return undefined;
};
var getDocumentId = function (ref) {
    var _a;
    if (ref && typeof ref === "object" && "id" in ref) {
        return (_a = ref.id) !== null && _a !== void 0 ? _a : "";
    }
    return "";
};
// Convert expense items to Sky Lifestyle format
var convertExpenseItems = function (items) {
    if (!items || !Array.isArray(items))
        return [];
    return items.map(function (item) { return ({
        key: item.description || "", // Use description, not ID
        value: item.amount || "0",
    }); });
};
var convertSkyExpensesToForm = function (items) {
    if (!items || !Array.isArray(items))
        return [];
    return items.map(function (item, index) {
        var description = (item === null || item === void 0 ? void 0 : item.key) || (item === null || item === void 0 ? void 0 : item.description) || "";
        // Detect corrupted description data (IDs saved as descriptions) and clear them
        var isCorruptedId = description && /^item_\d+(_item_\d+)*(_\d+)?$/.test(description);
        return {
            id: (item === null || item === void 0 ? void 0 : item.id) || "item_".concat(Date.now(), "_").concat(index), // Generate fresh ID
            description: isCorruptedId ? "" : description, // Clear corrupted IDs
            amount: (item === null || item === void 0 ? void 0 : item.value) || "0",
        };
    });
};
var normalizeBankStatements = function (input) {
    if (!input)
        return [];
    if (Array.isArray(input)) {
        return input.filter(function (value) {
            return typeof value === "string" && value.trim().length > 0;
        });
    }
    if (typeof input === "string" && input.trim().length > 0) {
        return [input.trim()];
    }
    return [];
};
// Convert legacy phone format to Sky Lifestyle format
var mapPhoneToSkyLifestyle = function (phone) {
    if (!phone) {
        return {
            country_abbreviation: DEFAULT_COUNTRY_ABBREVIATION,
            country_code: DEFAULT_COUNTRY_CODE,
            phone_number: "",
        };
    }
    return {
        country_abbreviation: phone.country_abbreviation || DEFAULT_COUNTRY_ABBREVIATION,
        country_code: phone.country_code || DEFAULT_COUNTRY_CODE,
        phone_number: sanitizePhoneNumber(phone.phone_number),
    };
};
exports.mapPhoneToSkyLifestyle = mapPhoneToSkyLifestyle;
var mapOptionalPhoneToSkyLifestyle = function (phone) {
    if (!hasPhoneNumber(phone))
        return undefined;
    return (0, exports.mapPhoneToSkyLifestyle)(phone || undefined);
};
// Map form data to Sky Lifestyle loan application structure
var mapFormDataToSkyLifestyle = function (formData) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    var now = new Date();
    var amount = typeof formData.loanAmount === "string"
        ? parseFloat(formData.loanAmount) || 0
        : Number((_a = formData.loanAmount) !== null && _a !== void 0 ? _a : 0);
    var bankStatements = normalizeBankStatements(formData.bankStatementsUrl);
    var latestPayslipUrl = typeof formData.latestPayslipUrl === "string"
        ? formData.latestPayslipUrl
        : "";
    var homeAddressInput = formData.homeAddress;
    var homeAddressFull = typeof homeAddressInput === "string"
        ? homeAddressInput
        : (homeAddressInput === null || homeAddressInput === void 0 ? void 0 : homeAddressInput.full_address) || "";
    var homeAddressLocation = formData.homeAddressGeoPoint ||
        (typeof homeAddressInput === "object"
            ? homeAddressInput === null || homeAddressInput === void 0 ? void 0 : homeAddressInput.location
            : undefined);
    var homeAddressErf = formData.homeErf ||
        (typeof homeAddressInput === "object"
            ? homeAddressInput === null || homeAddressInput === void 0 ? void 0 : homeAddressInput.homeErf
            : undefined);
    var homeAddress = __assign(__assign(__assign({ full_address: homeAddressFull || "" }, (homeAddressErf ? { homeErf: homeAddressErf } : {})), (homeAddressLocation ? { location: homeAddressLocation } : {})), { date_updated: now });
    var spouse = (function () {
        if (!formData.isMarried &&
            !formData.spouseFirstName &&
            !formData.spouseLastName &&
            !hasPhoneNumber(formData.spousePhoneNumber) &&
            !hasPhoneNumber(formData.spouseHomeNumber) &&
            !hasPhoneNumber(formData.spouseWorkNumber)) {
            return undefined;
        }
        var spouseData = {
            name: formData.spouseFirstName || "",
            surname: formData.spouseLastName || "",
            relationship: "SPOUSE",
            cell_number: (0, exports.mapPhoneToSkyLifestyle)(formData.spousePhoneNumber),
            date_updated: now,
        };
        // Only include optional phone numbers if they have values (Firestore doesn't allow undefined)
        var homeNumber = mapOptionalPhoneToSkyLifestyle(formData.spouseHomeNumber);
        if (homeNumber !== undefined) {
            spouseData.home_number = homeNumber;
        }
        var workNumber = mapOptionalPhoneToSkyLifestyle(formData.spouseWorkNumber);
        if (workNumber !== undefined) {
            spouseData.work_number = workNumber;
        }
        return spouseData;
    })();
    var familyMember = (function () {
        var hasData = formData.familyMemberName ||
            formData.familyMemberSurname ||
            formData.familyMemberRelationship ||
            hasPhoneNumber(formData.familyMemberCellPhone) ||
            hasPhoneNumber(formData.familyMemberHomePhone) ||
            hasPhoneNumber(formData.familyMemberWorkPhone);
        if (!hasData)
            return undefined;
        var familyMemberData = {
            name: formData.familyMemberName || "",
            surname: formData.familyMemberSurname || "",
            relationship: formData.familyMemberRelationship || "OTHER_RELATIVE",
            cell_number: (0, exports.mapPhoneToSkyLifestyle)(formData.familyMemberCellPhone),
            date_updated: now,
        };
        // Only include optional phone numbers if they have values (Firestore doesn't allow undefined)
        var homeNumber = mapOptionalPhoneToSkyLifestyle(formData.familyMemberHomePhone);
        if (homeNumber !== undefined) {
            familyMemberData.home_number = homeNumber;
        }
        var workNumber = mapOptionalPhoneToSkyLifestyle(formData.familyMemberWorkPhone);
        if (workNumber !== undefined) {
            familyMemberData.work_number = workNumber;
        }
        return familyMemberData;
    })();
    var friend = (function () {
        var hasData = formData.friendName ||
            formData.friendSurname ||
            hasPhoneNumber(formData.friendCellPhone) ||
            hasPhoneNumber(formData.friendHomePhone) ||
            hasPhoneNumber(formData.friendWorkPhone);
        if (!hasData)
            return undefined;
        var friendData = {
            name: formData.friendName || "",
            surname: formData.friendSurname || "",
            cell_number: (0, exports.mapPhoneToSkyLifestyle)(formData.friendCellPhone),
            date_updated: now,
        };
        // Only include optional phone numbers if they have values (Firestore doesn't allow undefined)
        var homeNumber = mapOptionalPhoneToSkyLifestyle(formData.friendHomePhone);
        if (homeNumber !== undefined) {
            friendData.home_number = homeNumber;
        }
        var workNumber = mapOptionalPhoneToSkyLifestyle(formData.friendWorkPhone);
        if (workNumber !== undefined) {
            friendData.work_number = workNumber;
        }
        return friendData;
    })();
    // Convert appointment_date from string to Date for Sky Lifestyle compatibility
    var appointmentDate = formData.appointmentDate
        ? new Date(formData.appointmentDate)
        : undefined;
    var employmentDetails = __assign(__assign(__assign(__assign({ employment_status: formData.employmentStatus || "", monthly_income: formData.monthlyIncome || "", employer: formData.employer || "", supervisor: formData.supervisorName || "", department: formData.departmentNumber || "", employee_number: formData.employeeNumber || "" }, (appointmentDate &&
        !isNaN(appointmentDate.getTime()) && {
        appointment_date: appointmentDate,
    })), { address: formData.workplaceAddress || "" }), (formData.workplaceAddressGeoPoint && {
        address_location: formData.workplaceAddressGeoPoint,
    })), { work_number: (0, exports.mapPhoneToSkyLifestyle)(formData.workNumber) });
    var financialDetail = {
        housing: formData.housingExpense || "0",
        transport: formData.transportExpense || "0",
        education: formData.educationExpense || "0",
        electricity: formData.electricityExpense || "0",
        groceries_and_other: formData.groceriesExpense || "0",
        account_payments: convertExpenseItems(formData.accountPayments), // Fixed typo: was "acccount_payments"
        fixed_monthly_deductions: convertExpenseItems(formData.fixedDeductions),
        other_obligations: convertExpenseItems(formData.otherObligations),
        payment_frequency: mapPayFrequencyToSkyLifestyle(formData.payFrequency),
        preferred_payday: formData.payDate || "",
    };
    // Map HR details to paypoint_details (Sky Web uses paymaster_number for "HR Name")
    // Note: Lewfin has separate optional Paymaster fields and required HR fields
    // Sky Web only has one set of fields labeled as "HR" but stored as paymaster_number
    var paypointDetails = __assign(__assign(__assign(__assign(__assign(__assign({ 
        // HR Name maps to paymaster_number (this is what Sky Web labels as "HR Name")
        paymaster_number: formData.hrName || "", 
        // HR Phone Number maps to phone_number (required field)
        phone_number: (0, exports.mapPhoneToSkyLifestyle)(formData.hrPhoneNumber), 
        // HR Postal Address
        postal_address: formData.hrPostalAddress || "" }, (formData.hrPostalAddressGeoPoint && {
        postal_address_location: formData.hrPostalAddressGeoPoint,
    })), { 
        // HR Physical Address
        physical_address: formData.hrPhysicalAddress || "" }), (formData.hrPhysicalAddressGeoPoint && {
        physical_address_location: formData.hrPhysicalAddressGeoPoint,
    })), { postal_code: formData.hrPostalCode || formData.postalCode || "", date_updated: now }), (formData.paymasterName && { paymaster_name: formData.paymasterName })), (hasPhoneNumber(formData.paymasterPhoneNumber) && {
        paymaster_phone: (0, exports.mapPhoneToSkyLifestyle)(formData.paymasterPhoneNumber),
    }));
    var salaryBank = {
        account_number: formData.accountNumber || "",
        account_type: formData.accountType || "",
        bank: formData.selectedBank || "",
        branch: formData.bankBranch || "",
        date_updated: now,
    };
    var payload = {
        amount: amount,
        term: formData.loanTerm || loan_application_1.DEFAULT_LOAN_TERM,
        status: formData.status || "DRAFT",
        is_product: !!formData.is_product,
        // NOTE: firstName, lastName, gender, id_number, is_married, marriage_type
        // are stored in USER profile, NOT in loan document (matching Sky Lifestyle)
        home_address: homeAddress,
        postal_address: formData.postalAddress || "",
        spouse: spouse,
        family_member: familyMember,
        friend: friend,
        employment_details: employmentDetails,
        financial_detail: financialDetail,
        paypoint_details: paypointDetails,
        salary_bank_details: salaryBank,
        personal_complete: !!formData.personal_complete,
        employment_complete: !!formData.employment_complete,
        financial_complete: !!formData.affordability_complete,
        paypoint_complete: !!((_b = formData.paypoint_complete) !== null && _b !== void 0 ? _b : formData.banking_complete),
        bankDetails_complete: !!((_c = formData.bankDetails_complete) !== null && _c !== void 0 ? _c : formData.banking_complete),
        docs_complete: !!((_d = formData.docs_complete) !== null && _d !== void 0 ? _d : (latestPayslipUrl || bankStatements.length > 0)),
        family_complete: (_e = formData.family_complete) !== null && _e !== void 0 ? _e : !!familyMember,
        friend_complete: (_f = formData.friend_complete) !== null && _f !== void 0 ? _f : !!friend,
        authoriseSalary_complete: !!formData.signatureURL,
        authoriseBank_complete: !!formData.bankSignatureURL,
        agree_terms: !!formData.confirmationChecked,
        sources: __assign({ initiated: "web", source: "web" }, ((_g = formData.sources) !== null && _g !== void 0 ? _g : {})),
    };
    if (formData.userId) {
        payload.user = (0, firestore_1.doc)(firebase_1.firestore, "users", formData.userId);
    }
    if (formData.providerId) {
        payload.provider = (0, firestore_1.doc)(firebase_1.firestore, "providers", formData.providerId);
    }
    if (formData.selectedBranch) {
        payload.branch = (0, firestore_1.doc)(firebase_1.firestore, "branches", formData.selectedBranch);
    }
    if ((_h = formData.forceNumber) === null || _h === void 0 ? void 0 : _h.trim()) {
        payload.force_number = formData.forceNumber.trim();
    }
    if ((_j = formData.orderId) === null || _j === void 0 ? void 0 : _j.trim()) {
        payload.order_id = formData.orderId.trim();
    }
    if ((_k = formData.orderNumber) === null || _k === void 0 ? void 0 : _k.trim()) {
        payload.order_number = formData.orderNumber.trim();
    }
    if ((_l = formData.loanNumber) === null || _l === void 0 ? void 0 : _l.trim()) {
        payload.loan_number = formData.loanNumber.trim();
    }
    if ((_m = formData.basketId) === null || _m === void 0 ? void 0 : _m.trim()) {
        payload.basket_id = formData.basketId.trim();
    }
    if (Array.isArray(formData.products) && formData.products.length > 0) {
        payload.products = formData.products;
    }
    if (latestPayslipUrl) {
        payload.latest_payslip = latestPayslipUrl;
    }
    if (bankStatements.length > 0) {
        payload.bank_statements = bankStatements;
    }
    if (formData.copyOfIdUrl) {
        payload.copy_of_id = formData.copyOfIdUrl;
    }
    if (formData.signatureURL) {
        payload.salary_deduction_signature = {
            signature: formData.signatureURL,
            date: now,
        };
    }
    if (formData.bankSignatureURL) {
        payload.bank_debit_signature = {
            signature: formData.bankSignatureURL,
            date: now,
        };
    }
    if (formData.dateCompleted) {
        payload.date_completed = formData.dateCompleted;
    }
    return payload;
};
exports.mapFormDataToSkyLifestyle = mapFormDataToSkyLifestyle;
// Map pay frequency to Sky Lifestyle format
var mapPayFrequencyToSkyLifestyle = function (frequency) {
    var frequencyMap = {
        weekly: "WEEKLY",
        bi_weekly: "BI-WEEKLY",
        monthly: "MONTHLY",
        daily: "DAILY",
    };
    return frequencyMap[frequency || "monthly"] || "MONTHLY";
};
// Map Sky Lifestyle data back to form format for editing
var mapSkyLifestyleToFormData = function (loanData) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28, _29, _30, _31, _32, _33;
    var bankStatements = Array.isArray(loanData.bank_statements)
        ? loanData.bank_statements
        : [];
    return {
        // Identifiers
        loanUid: loanData.uid || loanData.id || "",
        userId: getDocumentId(loanData.user),
        providerId: getDocumentId(loanData.provider),
        branchId: getDocumentId(loanData.branch),
        loanNumber: loanData.loan_number || "",
        orderNumber: loanData.order_number || "",
        orderId: loanData.order_id || "",
        basketId: loanData.basket_id || "",
        products: loanData.products || [],
        status: loanData.status || "DRAFT",
        // Basic information
        loanAmount: loanData.amount !== undefined ? loanData.amount.toString() : "0",
        loanTerm: (0, exports.normalizeLoanTerm)(loanData.term), // Normalizes both old and new formats
        selectedUser: getDocumentId(loanData.user),
        selectedBranch: getDocumentId(loanData.branch),
        selectedProvider: getDocumentId(loanData.provider),
        is_product: loanData.is_product || false,
        // Personal information (firstName, lastName, idNumber, gender, isMarried, marriageType
        // come from user profile, not from loan document - matching Sky Lifestyle)
        firstName: "", // Retrieved from user profile
        lastName: "", // Retrieved from user profile
        idNumber: "", // Retrieved from user profile
        homeAddress: ((_a = loanData.home_address) === null || _a === void 0 ? void 0 : _a.full_address) || "",
        homeErf: ((_b = loanData.home_address) === null || _b === void 0 ? void 0 : _b.homeErf) || "",
        homeAddressGeoPoint: ((_c = loanData.home_address) === null || _c === void 0 ? void 0 : _c.location) || null,
        postalAddress: loanData.postal_address || "",
        gender: "", // Retrieved from user profile
        isMarried: null, // Retrieved from user profile
        marriageType: "", // Retrieved from user profile
        // Spouse information
        spouseFirstName: ((_d = loanData.spouse) === null || _d === void 0 ? void 0 : _d.name) || "",
        spouseLastName: ((_e = loanData.spouse) === null || _e === void 0 ? void 0 : _e.surname) || "",
        spousePhoneNumber: mapSkyLifestylePhoneToForm((_f = loanData.spouse) === null || _f === void 0 ? void 0 : _f.cell_number),
        spouseHomeNumber: mapSkyLifestylePhoneToForm((_g = loanData.spouse) === null || _g === void 0 ? void 0 : _g.home_number),
        spouseWorkNumber: mapSkyLifestylePhoneToForm((_h = loanData.spouse) === null || _h === void 0 ? void 0 : _h.work_number),
        // Family Member
        familyMemberName: ((_j = loanData.family_member) === null || _j === void 0 ? void 0 : _j.name) || "",
        familyMemberSurname: ((_k = loanData.family_member) === null || _k === void 0 ? void 0 : _k.surname) || "",
        familyMemberRelationship: ((_l = loanData.family_member) === null || _l === void 0 ? void 0 : _l.relationship) || "",
        familyMemberCellPhone: mapSkyLifestylePhoneToForm((_m = loanData.family_member) === null || _m === void 0 ? void 0 : _m.cell_number),
        familyMemberHomePhone: mapSkyLifestylePhoneToForm((_o = loanData.family_member) === null || _o === void 0 ? void 0 : _o.home_number),
        familyMemberWorkPhone: mapSkyLifestylePhoneToForm((_p = loanData.family_member) === null || _p === void 0 ? void 0 : _p.work_number),
        // Friend Contact
        friendName: ((_q = loanData.friend) === null || _q === void 0 ? void 0 : _q.name) || "",
        friendSurname: ((_r = loanData.friend) === null || _r === void 0 ? void 0 : _r.surname) || "",
        friendCellPhone: mapSkyLifestylePhoneToForm((_s = loanData.friend) === null || _s === void 0 ? void 0 : _s.cell_number),
        friendHomePhone: mapSkyLifestylePhoneToForm((_t = loanData.friend) === null || _t === void 0 ? void 0 : _t.home_number),
        friendWorkPhone: mapSkyLifestylePhoneToForm((_u = loanData.friend) === null || _u === void 0 ? void 0 : _u.work_number),
        // Employment
        employmentStatus: ((_v = loanData.employment_details) === null || _v === void 0 ? void 0 : _v.employment_status) || "",
        monthlyIncome: ((_w = loanData.employment_details) === null || _w === void 0 ? void 0 : _w.monthly_income) || "",
        employer: ((_x = loanData.employment_details) === null || _x === void 0 ? void 0 : _x.employer) || "",
        supervisorName: ((_y = loanData.employment_details) === null || _y === void 0 ? void 0 : _y.supervisor) || "",
        departmentNumber: ((_z = loanData.employment_details) === null || _z === void 0 ? void 0 : _z.department) || "",
        employeeNumber: ((_0 = loanData.employment_details) === null || _0 === void 0 ? void 0 : _0.employee_number) || "",
        appointmentDate: (function () {
            var _a, _b;
            var date = (_a = loanData.employment_details) === null || _a === void 0 ? void 0 : _a.appointment_date;
            if (!date)
                return "";
            // Handle string (already in correct format), Date, or Timestamp
            if (typeof date === "string")
                return date.split("T")[0];
            return ((_b = toISOString(date)) === null || _b === void 0 ? void 0 : _b.split("T")[0]) || "";
        })(),
        workplaceAddress: ((_1 = loanData.employment_details) === null || _1 === void 0 ? void 0 : _1.address) || "",
        workplaceAddressGeoPoint: ((_2 = loanData.employment_details) === null || _2 === void 0 ? void 0 : _2.address_location) || null,
        workNumber: mapSkyLifestylePhoneToForm((_3 = loanData.employment_details) === null || _3 === void 0 ? void 0 : _3.work_number),
        // Financial / Affordability
        housingExpense: ((_4 = loanData.financial_detail) === null || _4 === void 0 ? void 0 : _4.housing) || "0",
        transportExpense: ((_5 = loanData.financial_detail) === null || _5 === void 0 ? void 0 : _5.transport) || "0",
        educationExpense: ((_6 = loanData.financial_detail) === null || _6 === void 0 ? void 0 : _6.education) || "0",
        electricityExpense: ((_7 = loanData.financial_detail) === null || _7 === void 0 ? void 0 : _7.electricity) || "0",
        groceriesExpense: ((_8 = loanData.financial_detail) === null || _8 === void 0 ? void 0 : _8.groceries_and_other) || "0",
        accountPayments: convertSkyExpensesToForm((_9 = loanData.financial_detail) === null || _9 === void 0 ? void 0 : _9.account_payments),
        fixedDeductions: convertSkyExpensesToForm((_10 = loanData.financial_detail) === null || _10 === void 0 ? void 0 : _10.fixed_monthly_deductions),
        otherObligations: convertSkyExpensesToForm((_11 = loanData.financial_detail) === null || _11 === void 0 ? void 0 : _11.other_obligations),
        payFrequency: mapSkyLifestylePayFrequencyToForm((_12 = loanData.financial_detail) === null || _12 === void 0 ? void 0 : _12.payment_frequency),
        payDate: ((_13 = loanData.financial_detail) === null || _13 === void 0 ? void 0 : _13.preferred_payday) || "",
        // HR/Paypoint Details (Sky Web stores HR data in paypoint_details)
        // paymaster_number in Sky = HR Name in Lewfin
        hrName: ((_14 = loanData.paypoint_details) === null || _14 === void 0 ? void 0 : _14.paymaster_number) || "",
        hrPhoneNumber: mapSkyLifestylePhoneToForm((_15 = loanData.paypoint_details) === null || _15 === void 0 ? void 0 : _15.phone_number),
        hrPostalAddress: ((_16 = loanData.paypoint_details) === null || _16 === void 0 ? void 0 : _16.postal_address) || "",
        hrPostalAddressGeoPoint: ((_17 = loanData.paypoint_details) === null || _17 === void 0 ? void 0 : _17.postal_address_location) || null,
        hrPhysicalAddress: ((_18 = loanData.paypoint_details) === null || _18 === void 0 ? void 0 : _18.physical_address) || "",
        hrPhysicalAddressGeoPoint: ((_19 = loanData.paypoint_details) === null || _19 === void 0 ? void 0 : _19.physical_address_location) || null,
        hrPostalCode: ((_20 = loanData.paypoint_details) === null || _20 === void 0 ? void 0 : _20.postal_code) || "",
        postalCode: ((_21 = loanData.paypoint_details) === null || _21 === void 0 ? void 0 : _21.postal_code) || "",
        // Optional Paymaster fields (if stored separately)
        paymasterName: ((_22 = loanData.paypoint_details) === null || _22 === void 0 ? void 0 : _22.paymaster_name) || "",
        paymasterPhoneNumber: mapSkyLifestylePhoneToForm((_23 = loanData.paypoint_details) === null || _23 === void 0 ? void 0 : _23.paymaster_phone),
        // Banking - prefer the canonical loan field, then support legacy records.
        selectedBank: ((_24 = loanData.salary_bank_details) === null || _24 === void 0 ? void 0 : _24.bank) || ((_25 = loanData.salary_bank) === null || _25 === void 0 ? void 0 : _25.bank) || "",
        bankBranch: ((_26 = loanData.salary_bank_details) === null || _26 === void 0 ? void 0 : _26.branch) ||
            ((_27 = loanData.salary_bank) === null || _27 === void 0 ? void 0 : _27.branch) ||
            "",
        accountNumber: ((_28 = loanData.salary_bank_details) === null || _28 === void 0 ? void 0 : _28.account_number) ||
            ((_29 = loanData.salary_bank) === null || _29 === void 0 ? void 0 : _29.account_number) ||
            "",
        accountType: ((_30 = loanData.salary_bank_details) === null || _30 === void 0 ? void 0 : _30.account_type) ||
            ((_31 = loanData.salary_bank) === null || _31 === void 0 ? void 0 : _31.account_type) ||
            "",
        // Documents
        latestPayslipUrl: loanData.latest_payslip || "",
        bankStatementsUrl: bankStatements,
        copyOfIdUrl: loanData.copy_of_id || "",
        // Signatures & authorisations
        signatureURL: ((_32 = loanData.salary_deduction_signature) === null || _32 === void 0 ? void 0 : _32.signature) || "",
        bankSignatureURL: ((_33 = loanData.bank_debit_signature) === null || _33 === void 0 ? void 0 : _33.signature) || "",
        // Completion flags
        personal_complete: !!loanData.personal_complete,
        employment_complete: !!loanData.employment_complete,
        affordability_complete: !!loanData.financial_complete,
        banking_complete: !!loanData.paypoint_complete,
        bankDetails_complete: !!loanData.bankDetails_complete,
        docs_complete: !!loanData.docs_complete,
        family_complete: !!loanData.family_complete,
        friend_complete: !!loanData.friend_complete,
        authoriseSalary_complete: !!loanData.authoriseSalary_complete,
        authoriseBank_complete: !!loanData.authoriseBank_complete,
        confirmationChecked: !!loanData.agree_terms,
        // Misc
        forceNumber: loanData.force_number || "",
        dateCompleted: loanData.date_completed
            ? toISOString(loanData.date_completed)
            : undefined,
        sources: loanData.sources || {},
    };
};
exports.mapSkyLifestyleToFormData = mapSkyLifestyleToFormData;
// Helper function to map Sky Lifestyle phone back to form format
var mapSkyLifestylePhoneToForm = function (phone) {
    if (!phone) {
        return createEmptyPhone();
    }
    return {
        phone_number: sanitizePhoneNumber(phone.phone_number),
        country_code: phone.country_code || DEFAULT_COUNTRY_CODE,
        country_abbreviation: phone.country_abbreviation ||
            DEFAULT_COUNTRY_ABBREVIATION,
    };
};
// Helper function to map Sky Lifestyle pay frequency back to form format
var mapSkyLifestylePayFrequencyToForm = function (frequency) {
    var frequencyMap = {
        WEEKLY: "weekly",
        "BI-WEEKLY": "bi_weekly",
        MONTHLY: "monthly",
        DAILY: "daily",
    };
    return frequencyMap[frequency || "MONTHLY"] || "monthly";
};
